import { LOCALE_STORAGE_KEY } from '@/i18n'
import {
	DEFAULT_LANGUAGE,
	isSupportedLanguage,
	LANGUAGE_LABELS,
	SUPPORTED_LANGUAGES,
	type SupportedLanguage,
} from '@/locales'
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react'
import { logger } from '@jobilla/instrumentation'
import { useTranslation } from 'react-i18next'

export function LanguageSelector() {
	const { t, i18n } = useTranslation('common')

	const currentLanguage: SupportedLanguage = isSupportedLanguage(i18n.language) ? i18n.language : DEFAULT_LANGUAGE

	function handleChange(language: SupportedLanguage) {
		const previousLanguage = i18n.language
		void i18n.changeLanguage(language)
		try {
			localStorage.setItem(LOCALE_STORAGE_KEY, language)
		} catch {
			// best-effort persistence
		}
		logger.track('Candidate Center: Changed language', {
			data: { language, previous_language: previousLanguage },
		})
	}

	return (
		<Listbox value={currentLanguage} onChange={handleChange}>
			<ListboxButton
				aria-label={t('language.aria', 'Change language')}
				className="group inline-flex items-center gap-1.5 rounded-lg border border-grey-200 bg-white py-1.5 pl-2.5 pr-2 text-sm font-medium text-grey-700 transition-colors hover:border-grey-300 focus:border-brand-500 focus:outline-none"
			>
				<GlobeIcon className="size-4 text-grey-500" />
				<span>{LANGUAGE_LABELS[currentLanguage]}</span>
				<ChevronDownIcon className="size-4 text-grey-400 transition-transform group-data-[open]:rotate-180" />
			</ListboxButton>

			<ListboxOptions
				anchor="bottom end"
				transition={true}
				className="z-50 max-h-[60vh] min-w-[var(--button-width)] overflow-auto rounded-lg border border-grey-200 bg-white py-1 shadow-lg [--anchor-gap:4px] origin-top transition duration-150 ease-out focus:outline-none data-closed:scale-95 data-closed:opacity-0"
			>
				{SUPPORTED_LANGUAGES.map((language) => (
					<ListboxOption
						key={language}
						value={language}
						className="group flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-grey-700 transition-colors data-[focus]:bg-grey-50 data-[selected]:font-medium data-[selected]:text-grey-900"
					>
						<CheckIcon className="size-4 text-brand-600 opacity-0 group-data-[selected]:opacity-100" />
						<span className="truncate">{LANGUAGE_LABELS[language]}</span>
					</ListboxOption>
				))}
			</ListboxOptions>
		</Listbox>
	)
}

function GlobeIcon({ className }: { className?: string }) {
	return (
		<svg
			className={className}
			fill="none"
			viewBox="0 0 24 24"
			stroke="currentColor"
			strokeWidth={1.8}
			aria-hidden="true"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm0 0c2.5 0 4.5-4.03 4.5-9S14.5 3 12 3 7.5 7.03 7.5 12s2 9 4.5 9ZM3.6 9h16.8M3.6 15h16.8"
			/>
		</svg>
	)
}

function ChevronDownIcon({ className }: { className?: string }) {
	return (
		<svg
			className={className}
			fill="none"
			viewBox="0 0 24 24"
			stroke="currentColor"
			strokeWidth={2}
			aria-hidden="true"
		>
			<path strokeLinecap="round" strokeLinejoin="round" d="m6 9 6 6 6-6" />
		</svg>
	)
}

function CheckIcon({ className }: { className?: string }) {
	return (
		<svg
			className={className}
			fill="none"
			viewBox="0 0 24 24"
			stroke="currentColor"
			strokeWidth={2.4}
			aria-hidden="true"
		>
			<path strokeLinecap="round" strokeLinejoin="round" d="m5 13 4 4L19 7" />
		</svg>
	)
}
